import { createColumnHelper } from '@tanstack/react-table';
import { useAtom } from 'jotai';
import {
  FC,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { RadioButton } from '@carbon/react';
import { DataTable } from 'components/data-table';
import type { DcaWorkspaceWellRegressionTaskApi } from 'types/dca-workspace-wells-api/responses';
import { workspaceWellSelectionMapAtomFamily } from './atoms';
import {
  jobLabel,
  jobRowId,
  wellTasksColumns,
  type WellTaskTableRow,
} from './well-jobs-columns';
import styles from './well-jobs-sub-table.module.scss';

type Props = {
  workspaceId: number;
  uwi: string;
  jobs: DcaWorkspaceWellRegressionTaskApi[];
  typeCurveFallback: string | null;
  typeCurveHref: string | null;
};

/**
 * Outermost `tableBodyContainer` on the ancestor chain (well grid scroller), skipping the
 * nested tasks DataTable's own `tableBodyContainer`.
 * @param {HTMLElement} start - Node inside the expanded row (e.g. the nested wrap).
 * @return {HTMLElement | null} Horizontal scroll clip host or null.
 */
function outermostTableBodyContainer(start: HTMLElement): HTMLElement | null {
  let cur: HTMLElement | null = start;
  let outer: HTMLElement | null = null;
  while (cur) {
    const cls = cur.getAttribute('class') ?? '';
    if (cls.includes('tableBodyContainer')) {
      outer = cur;
    }
    cur = cur.parentElement;
  }
  return outer;
}

/**
 * Expandable-row detail: regression tasks for one well jobs
 * @return {JSX.Element} Jobs table or empty state
 */
export const WellJobsSubTable: FC<Props> = ({
  workspaceId,
  uwi,
  jobs,
  typeCurveFallback,
  typeCurveHref,
}) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [tableMounted, setTableMounted] = useState(false);
  const [wellsMap, setWellsMap] = useAtom(
    workspaceWellSelectionMapAtomFamily(workspaceId)
  );

  const selectedForecastId = wellsMap.items[uwi]?.dcaForecastId ?? '';

  const latestForecastId = String(jobs.at(-1)?.wellDcaForecastId ?? '');

  const selectForecast = useCallback(
    (dcaForecastId: string) => {
      setWellsMap((prev) => ({
        ...prev,
        items: {
          ...prev.items,
          [uwi]: {
            ...prev.items[uwi],
            dcaForecastId,
            isLatestForecast: dcaForecastId === latestForecastId,
          },
        },
      }));
    },
    [setWellsMap, uwi, latestForecastId]
  );

  const data = useMemo<WellTaskTableRow[]>(
    () =>
      jobs.map((job, index) => ({
        ...job,
        rowId: jobRowId(job, index),
        typeCurveDisplay: typeCurveFallback ?? '',
        typeCurveHref,
      })),
    [jobs, typeCurveFallback, typeCurveHref]
  );

  useLayoutEffect(() => {
    if (!data.length) {
      setTableMounted(false);
      return undefined;
    }
    const wrap = wrapRef.current;
    if (!wrap) return undefined;

    const measure = () => {
      const host =
        outermostTableBodyContainer(wrap) ?? document.documentElement;
      const w = host.clientWidth;
      wrap.style.maxWidth = w > 0 ? `${w}px` : '';
    };

    measure();
    setTableMounted(true);

    const host = outermostTableBodyContainer(wrap);
    const ro = new ResizeObserver(measure);
    ro.observe(wrap);
    if (host) ro.observe(host);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
      wrap.style.maxWidth = '';
    };
  }, [data.length]);

  const persistenceKey = `dca-workspace-well-tasks-${workspaceId}-${encodeURIComponent(
    uwi
  )}`;

  const radioGroupName = `dca-workspace-${workspaceId}-well-${uwi}-task`;

  const taskColumns = useMemo(() => {
    const columnHelper = createColumnHelper<WellTaskTableRow>();
    return [
      columnHelper.display({
        id: 'jobSelect',
        header: () => <span className={styles.srOnly}>Select job</span>,
        cell: ({ row }) => {
          const fid = String(row.original.wellDcaForecastId ?? '');
          const inputId = `${radioGroupName}-${row.original.rowId}`;
          return (
            <RadioButton
              id={inputId}
              labelText={`Select job ${jobLabel(row.original.jobId)}`}
              hideLabel
              value={fid}
              name={radioGroupName}
              checked={!!fid && selectedForecastId === fid}
              onChange={() => selectForecast(fid)}
            />
          );
        },
        size: 48,
        minSize: 48,
        maxSize: 48,
        enableHiding: false,
        enableColumnFilter: false,
        enableGlobalFilter: false,
        enableResizing: false,
        meta: {
          toggleColumnFilter: false,
          enablePinning: false,
          enableReordering: false,
        },
      }),
      ...wellTasksColumns,
    ];
  }, [radioGroupName, selectedForecastId, selectForecast]);

  if (!data.length) {
    return <p className={styles.empty}>No tasks for this well.</p>;
  }

  return (
    <div ref={wrapRef} className={styles.nestedTableWrap}>
      {tableMounted ? (
        <DataTable<WellTaskTableRow & Record<string, unknown>>
          data={data as (WellTaskTableRow & Record<string, unknown>)[]}
          columns={taskColumns}
          getRowId={(row) => row.rowId}
          persistenceKey={persistenceKey}
          searchPlaceholder="Search jobs…"
          initialTableState={{ columnPinning: { left: ['jobSelect'] } }}
          // The DataTable selection mirrors the atom-backed `selectedForecastId` — the real
          // control is the `jobSelect` radio, which reads/writes the atom directly. Controlled
          // so it tracks the atom without the reactive-seed path.
          controlledState={{
            selection: {
              selectAll: false,
              toggledNodes: selectedForecastId
                ? (data
                    .filter(
                      (job) =>
                        String(job.wellDcaForecastId ?? '') ===
                        selectedForecastId
                    )
                    .map((job) => job.rowId) as string[])
                : [],
            },
          }}
          config={{
            sorting: true,
            hideFooter: true,
            globalSearch: true,
            columnFilters: true,
            columnVisibility: true,
            columnPinning: true,
            columnReordering: true,
            columnResizing: true,
            export: false,
          }}
        />
      ) : null}
    </div>
  );
};
